import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import PermissionResourceSchema from "permissions/resources/_schemas/permission-resource-schema";
import { AccessLevel, ResourceType, Scope } from "permissions/resources/permission-resource-types";
import { z } from "zod";

export type CurrencyFieldsAccessJSON = z.infer<typeof CurrencyFieldsAccessSchema.CurrencyFieldsAccessJSONZ>;

export type CurrencyFieldsAccessParams = z.infer<typeof CurrencyFieldsAccessSchema.CurrencyFieldsAccessZ>;

export default class CurrencyFieldsAccessSchema {
  public static readonly CurrencyFieldsAccessJSONZ = z.object({
    ...PermissionResourceSchema.ResourceJSONZ.shape,
    resourceType: z.literal(ResourceType.CurrencyFieldsAccess),
    accessLevel: z.enum(AccessLevel).nullish(),
    scope: z.enum(Scope).nullish(),
  });

  public static readonly CurrencyFieldsAccessZ = z.object({
    ...PermissionResourceSchema.ResourceZ.shape,
    resourceType: z.literal(ResourceType.CurrencyFieldsAccess),
    accessLevel: z.enum(AccessLevel).optional(),
    scope: z.enum(Scope).optional(),
  });

  public static validate(values: unknown): CurrencyFieldsAccessParams {
    return parseOrThrow(CurrencyFieldsAccessSchema.CurrencyFieldsAccessZ, values, {
      logError: true,
      schemaName: "CurrencyFieldsAccessSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): CurrencyFieldsAccessJSON {
    return parseOrThrow(CurrencyFieldsAccessSchema.CurrencyFieldsAccessJSONZ, values, {
      logError: true,
      schemaName: "CurrencyFieldsAccessSchema",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is CurrencyFieldsAccessParams {
    try {
      CurrencyFieldsAccessSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(values: unknown): values is CurrencyFieldsAccessJSON {
    try {
      CurrencyFieldsAccessSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: CurrencyFieldsAccessJSON): CurrencyFieldsAccessParams {
    const parsed = parseOrThrow(CurrencyFieldsAccessSchema.CurrencyFieldsAccessJSONZ, data, {
      logError: true,
      schemaName: "CurrencyFieldsAccessSchema",
      operation: "deserialize",
      payloadSample: data,
    });
    return CurrencyFieldsAccessSchema.codec.decode(parsed);
  }

  public static serialize(notification: CurrencyFieldsAccessParams): CurrencyFieldsAccessJSON {
    const parsed = CurrencyFieldsAccessSchema.validate(notification);
    return CurrencyFieldsAccessSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    CurrencyFieldsAccessSchema.CurrencyFieldsAccessJSONZ,
    CurrencyFieldsAccessSchema.CurrencyFieldsAccessZ,
    {
      decode: (data) => ({
        ...PermissionResourceSchema.deserialize(data),
        resourceType: ResourceType.CurrencyFieldsAccess as const,
        accessLevel: data.accessLevel ?? undefined,
        scope: data.scope ?? undefined,
      }),
      encode: (resource) => ({
        ...PermissionResourceSchema.serialize(resource),
        resourceType: ResourceType.CurrencyFieldsAccess as const,
        accessLevel: resource.accessLevel,
        scope: resource.scope,
      }),
    }
  );
}
